import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Alert, Pressable, ScrollView, View} from 'react-native';
import {Button} from 'react-native-paper';
import {Text} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import {localizedModuleTitle} from '../../Models/moduleTitles.i18n';
import {
  AssessmentLanguageCode,
  AssessmentResultSummary,
  loadCompletedAssessments,
} from '../../Utils/Assessment';
import {
  ClinicalIndicatorLevel,
  formatClinicalNote,
  getClinicalIndicatorLevel,
  getClinicalOverallPresentation,
  getTopClinicalVerdict,
  isClinicianGuidedAssessment,
  orderClinicalVerdicts,
} from '../../Utils/ClinicalResults';
import {shareAdolescentSummary} from '../../Utils/Exports';
import {getCopy} from '../../Utils/I18n';
import {trackEvent} from '../../Utils/Telemetry';
import {RootResultsScreenProps} from '../../Typings/NavigationTypes';
import getStyles from './ADAPT-MH.styles';

type Props = RootResultsScreenProps;

const getLegacyVerdictStatus = (verdict: {criteriaMet: boolean; determined: boolean}): string => {
  if (verdict.criteriaMet) {
    return 'May meet criteria — worth discussing with someone';
  }
  return verdict.determined ? 'Criteria not met' : 'Not enough information';
};

const Results: React.FC<Props> = ({navigation, route}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [isHidden, setIsHidden] = useState(false);
  const [summary, setSummary] = useState<AssessmentResultSummary | undefined>(route.params.summary);
  const [language, setLanguage] = useState<AssessmentLanguageCode>(route.params.language || 'en');
  const [isClinical, setIsClinical] = useState<boolean | undefined>(route.params.clinical);
  const hasTrackedViewRef = useRef(false);
  const {sessionId} = route.params;
  const copy = getCopy(language);
  const showClinicalResults = Boolean(
    isClinical && summary?.moduleVerdicts && summary.moduleVerdicts.length > 0,
  );

  useEffect(() => {
    const loadSummary = async () => {
      if (summary && isClinical !== undefined) return;

      const records = await loadCompletedAssessments();
      const matchingRecord = records.find((record) => record.sessionId === sessionId);
      if (!summary) {
        setSummary(matchingRecord?.resultSummary);
      }
      if (matchingRecord?.language) {
        setLanguage(matchingRecord.language);
      }
      if (isClinical === undefined) {
        setIsClinical(matchingRecord ? isClinicianGuidedAssessment(matchingRecord) : false);
      }
    };

    loadSummary();
  }, [isClinical, sessionId, summary]);

  useEffect(() => {
    if (!summary || isClinical === undefined || hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    trackEvent('results_viewed', {
      sessionId,
      language,
      payload: {clinical: Boolean(isClinical), severityLevel: summary.severityLevel},
    });
  }, [isClinical, language, sessionId, summary]);

  useLayoutEffect(() => {
    navigation.setOptions({
      title: showClinicalResults ? copy.results.clinical.title : '',
    });
  }, [copy.results.clinical.title, navigation, showClinicalResults]);

  if (!summary || isClinical === undefined) {
    return (
      <PageContainer withSafeArea style={styles.webResultsPage}>
        <View style={[styles.webResultCard, styles.webHiddenBox]}>
          <Text weight="semiBold" style={styles.strongBody}>
            {copy.results.loading}
          </Text>
        </View>
      </PageContainer>
    );
  }

  const isUrgent = summary.riskFlag.triggered;
  const onSharePress = async () => {
    try {
      await shareAdolescentSummary(summary);
    } catch (error) {
      console.error('Failed to share result summary', error);
      Alert.alert('Could not share summary', 'Please try again from this results page.');
    }
  };

  if (showClinicalResults && summary.moduleVerdicts) {
    const clinicalCopy = copy.results.clinical;
    const orderedVerdicts = orderClinicalVerdicts(summary.moduleVerdicts);
    const overallPresentation = getClinicalOverallPresentation(summary.severityLevel);
    const topVerdict = getTopClinicalVerdict(orderedVerdicts);
    const topCondition = topVerdict
      ? localizedModuleTitle(topVerdict.target, language, topVerdict.title)
      : summary.topConcernLabel;
    const clinicalNote = formatClinicalNote(
      clinicalCopy.noteTemplates[overallPresentation.level],
      topCondition,
    );
    const overallWidth = `${overallPresentation.progress * 100}%`;

    const getBadgeStyles = (level: ClinicalIndicatorLevel) => {
      if (level === 'moderate') {
        return [styles.clinicalIndicatorBadge, styles.clinicalIndicatorModerate];
      }
      if (level === 'mild') {
        return [styles.clinicalIndicatorBadge, styles.clinicalIndicatorMild];
      }
      return [styles.clinicalIndicatorBadge, styles.clinicalIndicatorLow];
    };

    const getBadgeTextStyle = (level: ClinicalIndicatorLevel) => [
      styles.clinicalIndicatorBadgeText,
      level === 'moderate' ? styles.clinicalIndicatorModerateText : null,
      level === 'mild' ? styles.clinicalIndicatorMildText : null,
      level === 'low' ? styles.clinicalIndicatorLowText : null,
    ];

    return (
      <PageContainer withSafeArea style={styles.clinicalResultsPage}>
        <ScrollView contentContainerStyle={styles.clinicalResultsContent}>
          <View style={[styles.clinicalResultCard, styles.clinicalConditionCard]}>
            <View style={styles.clinicalCardHeader}>
              <View
                style={[styles.clinicalSectionAccent, styles.clinicalConditionAccent]}
                importantForAccessibility="no"
              />
              <Text
                weight="bold"
                fontSize={17}
                accessibilityRole="header"
                style={[styles.clinicalSectionTitle, styles.clinicalConditionTitle]}
              >
                {clinicalCopy.conditionIndicators}
              </Text>
            </View>

            {orderedVerdicts.map((verdict) => {
              const level = getClinicalIndicatorLevel(verdict);
              const levelLabel = clinicalCopy.indicatorLevels[level];
              const moduleTitle = localizedModuleTitle(verdict.target, language, verdict.title);

              return (
                <View
                  key={verdict.target}
                  accessible
                  accessibilityLabel={`${moduleTitle}: ${levelLabel}`}
                  style={styles.clinicalIndicatorRow}
                >
                  <Text fontSize={15} weight="semiBold" style={styles.clinicalIndicatorName}>
                    {moduleTitle}
                  </Text>
                  <View style={getBadgeStyles(level)}>
                    <Text
                      weight="bold"
                      fontSize={13}
                      align="center"
                      style={getBadgeTextStyle(level)}
                    >
                      {levelLabel}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>

          <View style={[styles.clinicalResultCard, styles.clinicalOverallCard]}>
            <View style={styles.clinicalOverallHeader}>
              <View style={[styles.clinicalCardHeader, styles.clinicalOverallHeaderTitle]}>
                <View
                  style={[styles.clinicalSectionAccent, styles.clinicalOverallAccent]}
                  importantForAccessibility="no"
                />
                <Text
                  weight="bold"
                  fontSize={17}
                  accessibilityRole="header"
                  style={[styles.clinicalSectionTitle, styles.clinicalOverallTitle]}
                >
                  {clinicalCopy.overallSeverity}
                </Text>
              </View>
              <View style={styles.clinicalOverallBadge}>
                <Text weight="bold" fontSize={13} style={styles.clinicalOverallLabel}>
                  {clinicalCopy.overallLevels[overallPresentation.level]}
                </Text>
              </View>
            </View>
            <View style={styles.clinicalOverallTrack}>
              <View style={[styles.clinicalOverallFill, {width: overallWidth}]} />
            </View>
            <Text style={styles.clinicalAdvisory}>{clinicalCopy.advisory}</Text>
          </View>

          <View style={[styles.clinicalResultCard, styles.clinicalNoteCard]}>
            <View style={styles.clinicalCardHeader}>
              <View
                style={[styles.clinicalSectionAccent, styles.clinicalNoteAccent]}
                importantForAccessibility="no"
              />
              <Text
                weight="bold"
                fontSize={17}
                accessibilityRole="header"
                style={[styles.clinicalSectionTitle, styles.clinicalNoteTitle]}
              >
                {clinicalCopy.clinicalNote}
              </Text>
            </View>
            <Text fontSize={15} style={styles.clinicalNoteBody}>
              {clinicalNote}
            </Text>
          </View>

          <Button
            mode="contained"
            onPress={() => navigation.navigate('Support', {urgent: isUrgent, sessionId, language})}
            style={styles.clinicalCareButton}
            contentStyle={styles.clinicalCareButtonContent}
            labelStyle={styles.clinicalCareButtonLabel}
            buttonColor={theme.colors.primary}
            textColor={theme.colors.textContrast}
          >
            {clinicalCopy.careGuidelines}
          </Button>
        </ScrollView>
      </PageContainer>
    );
  }

  return (
    <PageContainer withSafeArea style={styles.webResultsPage}>
      <ScrollView contentContainerStyle={styles.webResultsContent}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={isHidden ? copy.results.show : copy.results.hide}
          onPress={() => setIsHidden((prev) => !prev)}
          style={styles.webResultsToggle}
        >
          <Text weight="bold" fontSize={13} style={styles.webResultsToggleText}>
            {isHidden ? copy.results.show : copy.results.hide}
          </Text>
        </Pressable>

        {isHidden ? (
          <View style={[styles.webResultCard, styles.webHiddenBox]}>
            <Text weight="bold" fontSize={18} style={styles.strongBody}>
              {copy.results.hiddenTitle}
            </Text>
            <Text align="center" style={styles.webResultBody}>
              {copy.results.hiddenBody}
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.webResultCard}>
              <Text
                fontSize={24}
                lineHeight={31}
                weight="bold"
                accessibilityRole="header"
                style={styles.webResultHeadline}
              >
                {summary.headline}
              </Text>
              <Text fontSize={15} lineHeight={23} style={styles.webResultSubtitle}>
                {summary.explanation}
              </Text>
            </View>

            <View style={styles.webResultCard}>
              <Text weight="bold" style={styles.webResultSectionTitle}>
                {copy.results.nextStep}
              </Text>
              <Text style={styles.webResultBody}>{summary.suggestedAction}</Text>
            </View>

            {summary.moduleVerdicts && summary.moduleVerdicts.length > 0 ? (
              <View style={styles.webResultCard}>
                <Text weight="bold" style={styles.webResultSectionTitle}>
                  What we looked at
                </Text>
                {summary.moduleVerdicts.map((verdict) => {
                  const status = getLegacyVerdictStatus(verdict);
                  return (
                    <Text key={verdict.target} style={styles.webResultListItem}>
                      {`- ${verdict.title}: ${status}`}
                    </Text>
                  );
                })}
              </View>
            ) : null}

            <View style={styles.webResultCard}>
              <Text style={styles.webResultBody}>{copy.results.disclaimer}</Text>
            </View>
          </>
        )}

        <View style={styles.webResultActions}>
          <Button
            mode="contained"
            onPress={() => navigation.navigate('Support', {urgent: isUrgent, sessionId, language})}
            style={styles.webPrimaryButton}
            contentStyle={styles.webButtonContent}
            labelStyle={styles.webButtonLabel}
            buttonColor={theme.colors.primary}
            textColor={theme.colors.textContrast}
          >
            {isUrgent ? copy.results.getSupportNow : copy.results.seeSupport}
          </Button>

          <Button
            mode="outlined"
            onPress={onSharePress}
            style={styles.webSecondaryButton}
            contentStyle={styles.webButtonContent}
            labelStyle={styles.webSecondaryButtonLabel}
            textColor={theme.colors.primary}
          >
            Save / share private summary
          </Button>

          <Button
            mode="outlined"
            onPress={() => navigation.navigate('Landing')}
            style={styles.webSecondaryButton}
            contentStyle={styles.webButtonContent}
            labelStyle={styles.webSecondaryButtonLabel}
            textColor={theme.colors.primary}
          >
            {copy.results.backToStart}
          </Button>
        </View>
      </ScrollView>
    </PageContainer>
  );
};

export default Results;
